import type {GridFilterItem as MuiGridFilterItem} from '@mui/x-data-grid'
import {format} from 'date-fns'
import {parse, toSeconds} from 'iso8601-duration'
import type {FetchBaseQueryError} from '@reduxjs/toolkit/query'
import {baseApi} from './base'
import type {BaseFetchResultInterface, BaseFetchResultsInterface} from '@interfaces/base'
import {addValueLabels, toPaginatedResponse} from './responseTransforms'
import type {BatchBasicInterface, BatchInterface} from '@interfaces/batch.ts'
import type {AsyncTaskScheduledInterface} from '@interfaces/common/asyncTaskScheduled.ts'
import type {EquipmentTraceInterface} from '@interfaces/equipmentTrace.ts'
import type {ProductionTimelineInterface} from '@interfaces/productionTimeline.ts'
import type {
  ProgramSignalThresholdCreateBody,
  ProgramSignalThresholdInterface,
  ProgramSignalThresholdViolationInterface,
  ThresholdCheckpointResetResult,
} from '@interfaces/programSignalThreshold.ts'
import type {AggregatorTermInterface, DataType, SignalInterface} from '@interfaces/signal.ts'
import type {
  SignalDescriptionValueInterface,
  SignalDescriptionValueUploadResponse,
} from '@interfaces/signalDescriptionValue.ts'
import type {
  SignalValueLabelsInterface,
  TraceDataInterface,
  TraceInterface,
  TraceResponseInterface,
} from '@interfaces/trace.ts'
import {operatorToLookup} from '@app/shared/components/base/filters/datagridHelpers'
import type {SignalLinkInterface} from '@interfaces/signalLink'
import type {TimelineLaneInterval} from '@containers/Dashboards/EquipmentDashboard/components/productionTimeline/signalTimelineLanes'

/**
 * Signals by id, all of them. The signals endpoint pages at the platform default
 * of 25 and does not know `no_pagination` (only the mock handlers do), so a
 * dashboard whose live widgets name more signals got the first 25 and showed
 * "No live values available" for the rest (AB#6712). `limit` asks for all of them
 * in one page; `no_pagination` stays for the mocks.
 */
export const signalsByIdsUrl = (signalIds: string[]): string =>
  `signals/?id__in=${encodeURIComponent(signalIds.join(','))}` +
  `&limit=${Math.max(signalIds.length, 1)}&no_pagination=true`

const PROGRAM_SIGNAL_THRESHOLD_ORDERING_MAP: Record<string, string> = {
  equipment_name: 'equipment_name_ci',
  name: 'name_ci',
}

const PROGRAM_SIGNAL_THRESHOLD_VIOLATION_ORDERING_MAP: Record<string, string> = {
  equipment_name: 'equipment_name_ci',
  program_signal_threshold: 'program_signal_threshold_name_ci',
  t_start: 't_start',
  t_end: 't_end',
}

const mapOrdering = (
  ordering: string | undefined,
  fieldMap: Record<string, string>,
): string | undefined => {
  if (!ordering) {
    return undefined
  }
  const isDesc = ordering.startsWith('-')
  const field = ordering.replace(/^-/, '')
  const mappedField = fieldMap[field] || field
  return `${isDesc ? '-' : ''}${mappedField}`
}

export interface BatchTraceQueryArgs {
  svtValue: string | null
  svtAnchorMode?: boolean
  expandRoot?: boolean
  equipmentId: string
  traceProgramClassificationIds?: string[]
  includeLinkTypeIds?: string[]
  excludeLinkTypeIds?: string[]
  initialSvtTStart?: string | null
  initialSvtTEnd?: string | null
  svtCandidatesPage?: number
  svtCandidatesPageSize?: number
  svtMaxCandidates?: number
  svtLookbackDays?: number | null
  tStart?: string | null
  tEnd?: string | null
  traceDirection: string
  maxDepth?: number | null
  // Both directions only (AB#6723): each walk's own depth.
  upstreamMaxDepth?: number | null
  downstreamMaxDepth?: number | null
}

export const buildBatchTraceQueryString = ({
  svtValue,
  svtAnchorMode,
  expandRoot,
  equipmentId,
  traceProgramClassificationIds,
  includeLinkTypeIds,
  excludeLinkTypeIds,
  initialSvtTStart,
  initialSvtTEnd,
  svtCandidatesPage,
  svtCandidatesPageSize,
  svtMaxCandidates,
  svtLookbackDays,
  tStart,
  tEnd,
  traceDirection,
  maxDepth,
  upstreamMaxDepth,
  downstreamMaxDepth,
}: BatchTraceQueryArgs): string => {
  const params = new URLSearchParams({
    initial_equipment_id: equipmentId,
    trace_direction: traceDirection,
  })

  if (tStart) params.set('t_start', tStart)
  if (tEnd) params.set('t_end', tEnd)
  if (typeof maxDepth === 'number') params.set('max_depth', maxDepth.toString())
  if (typeof upstreamMaxDepth === 'number')
    params.set('upstream_max_depth', upstreamMaxDepth.toString())
  if (typeof downstreamMaxDepth === 'number')
    params.set('downstream_max_depth', downstreamMaxDepth.toString())
  if (svtValue) params.set('svt_value', svtValue)
  if (svtAnchorMode === true) params.set('svt_anchor_mode', 'true')
  if (expandRoot === true) params.set('expand_root', 'true')
  traceProgramClassificationIds?.forEach((id) =>
    params.append('trace_program_classification_ids', id),
  )
  includeLinkTypeIds?.forEach((id) => params.append('include_link_type_ids', id))
  excludeLinkTypeIds?.forEach((id) => params.append('exclude_link_type_ids', id))
  if (initialSvtTStart) params.set('initial_svt_t_start', initialSvtTStart)
  if (initialSvtTEnd) params.set('initial_svt_t_end', initialSvtTEnd)
  if (typeof svtCandidatesPage === 'number')
    params.set('svt_candidates_page', svtCandidatesPage.toString())
  if (typeof svtCandidatesPageSize === 'number')
    params.set('svt_candidates_page_size', svtCandidatesPageSize.toString())
  if (typeof svtMaxCandidates === 'number')
    params.set('svt_max_candidates', svtMaxCandidates.toString())
  if (typeof svtLookbackDays === 'number')
    params.set('svt_lookback_days', svtLookbackDays.toString())

  return `trace_material_flow/?${params.toString()}`
}

export interface SignalsLeanQueryArgs {
  lineId: string | undefined
  equipmentId: string
  limit: number
  offset?: number
  noPagination?: boolean
  search?: string
  // Server-side favourites filter (signals/lean supports ?is_favorite=).
  isFavorite?: boolean
  // Server-side timeline-lane filter (?show_in_timeline=, AB#5666).
  showInTimeline?: boolean
  // Server-side sample-strip filter (?show_in_timeline_as_sample=, AB#6168).
  showInTimelineAsSample?: boolean
  // Multiple equipments at once (batch trace's state lanes, AB#6246): the
  // trace spans equipments that may sit on different lines, so a single
  // machine/machine_sub_system pair can't scope the request. Sent as
  // machine_sub_system__in -- the signals/lean filterset already declares
  // "in" for machine_sub_system, so this is the existing CSV `__in` lookup
  // DjangoFilterBackend auto-generates, the same pattern already used
  // elsewhere against this API (e.g. threshold violations by equipment), not
  // a new backend capability. Takes precedence over the singular
  // equipmentId/lineId when non-empty.
  equipmentIds?: string[]
  // Only these data types (?data_type__in=).
  dataTypes?: readonly string[]
}

export const buildSignalsLeanQuery = ({
  lineId,
  equipmentId,
  limit,
  offset,
  noPagination,
  search,
  isFavorite,
  showInTimeline,
  showInTimelineAsSample,
  equipmentIds,
  dataTypes,
}: SignalsLeanQueryArgs): string => {
  const hasMultipleEquipmentIds = Boolean(equipmentIds && equipmentIds.length > 0)
  const machineParam = !hasMultipleEquipmentIds && lineId ? `&machine=${lineId}` : ''
  const machineSubSystemParam = hasMultipleEquipmentIds
    ? `&machine_sub_system__in=${equipmentIds!.join(',')}`
    : equipmentId
      ? `&machine_sub_system=${equipmentId}`
      : ''
  const limitParam = `&limit=${limit}`
  const offsetParam = offset !== undefined ? `&offset=${offset}` : ''
  const noPaginationParam = noPagination ? '&no_pagination=true' : ''
  const searchParam = search ? `&search=${encodeURIComponent(search)}` : ''
  const favoriteParam = isFavorite ? '&is_favorite=true' : ''
  const showInTimelineParam = showInTimeline ? '&show_in_timeline=true' : ''
  const sampleParam = showInTimelineAsSample ? '&show_in_timeline_as_sample=true' : ''
  const dataTypesParam = dataTypes?.length ? `&data_type__in=${dataTypes.join(',')}` : ''

  // Start with base URL and add parameters only if they're non-empty
  const params = [
    machineParam,
    machineSubSystemParam,
    limitParam,
    offsetParam,
    noPaginationParam,
    searchParam,
    favoriteParam,
    showInTimelineParam,
    sampleParam,
    dataTypesParam,
  ]
    .filter(Boolean)
    .join('')

  return `signals/lean/${params ? `?${params.substring(1)}` : ''}`
}

// `representation=points` reads at most this many signal_ids per request
// (MAX_POINTS_SIGNAL_IDS in pm-core's signal_values_serializer).
export const POINTS_MAX_SIGNAL_IDS = 100

// `representation=on_change` reads at most this many signal_ids per request
// (MAX_ON_CHANGE_SIGNAL_IDS in pm-core's signal_values_serializer).
export const ON_CHANGE_MAX_SIGNAL_IDS = 50

// Signal ids per signal-links/ request: they travel in the query string.
export const SIGNAL_LINKS_MAX_SIGNAL_IDS = 50

interface SignalIntervalsResponse {
  truncated?: boolean
  signals?: Array<{
    signal_id: string
    intervals?: Array<{value: string | number; t_start: string; t_end: string | null}>
    labels?: SignalValueLabelsInterface
    error?: string
  }>
}

// Per-signal value labels of a signals/values/ read (AB#6168): signal id ->
// String(value) -> description / admin colour, for the values it returned.
export interface ValueLabelsBySignal {
  labelsBySignal: Record<string, Record<string, string>>
  colorsBySignal: Record<string, Record<string, string>>
}

const chunkIds = (ids: string[], size: number): string[][] => {
  const chunks: string[][] = []
  for (let start = 0; start < ids.length; start += size) {
    chunks.push(ids.slice(start, start + size))
  }
  return chunks
}

// Signal ids per signals/lean/?ids= request, for the same query-string reason.
export const SIGNALS_LEAN_BY_IDS_MAX_SIGNAL_IDS = 50

// Target-side page size of signal-links/: one limit signal can bound many
// measurements (a shared spec), so unlike the owner side there is no per-id cap.
export const SIGNAL_LINKS_PAGE_SIZE = 500

type SignalLinkPageFetch<E> = (
  url: string,
) => PromiseLike<{data?: unknown; error?: E}> | {data?: unknown; error?: E}

/**
 * Every active SignalLink matching `filterParam=<ids>`, chunked because the
 * ids travel in the query string and paged until the count is reached. One
 * failed request fails the read: links missing for a random chunk would render
 * those signals as unlinked and look complete.
 */
const readSignalLinks = async <E>(
  fetchPage: SignalLinkPageFetch<E>,
  filterParam: 'signals' | 'related_signal',
  signalIds: string[],
  pageSize: (chunk: string[]) => number,
): Promise<{data: SignalLinkInterface[]} | {error: E}> => {
  const readChunk = async (
    chunk: string[],
  ): Promise<{data: SignalLinkInterface[]} | {error: E}> => {
    const links: SignalLinkInterface[] = []
    let offset = 0
    for (;;) {
      const params = new URLSearchParams()
      params.set(filterParam, chunk.join(','))
      params.set('limit', String(pageSize(chunk)))
      if (offset > 0) {
        params.set('offset', String(offset))
      }
      // Sequential on purpose: the next offset depends on this page's count.
      // eslint-disable-next-line no-await-in-loop
      const response = await fetchPage(`signal-links/?${params.toString()}`)
      if (response.error) {
        return {error: response.error}
      }
      const body = response.data as
        | {count?: number; results?: SignalLinkInterface[]}
        | SignalLinkInterface[]
      if (Array.isArray(body)) {
        links.push(...body)
        return {data: links}
      }
      const page = body.results ?? []
      links.push(...page)
      offset += page.length
      if (page.length === 0 || offset >= (body.count ?? 0)) {
        return {data: links}
      }
    }
  }

  const results = await Promise.all(
    chunkIds(signalIds, SIGNAL_LINKS_MAX_SIGNAL_IDS).map((chunk) => readChunk(chunk)),
  )
  const links: SignalLinkInterface[] = []
  for (const result of results) {
    if ('error' in result) {
      return result
    }
    links.push(...result.data)
  }
  return {data: links}
}

// Signal ids per batched threshold / violation request: the ids travel in the
// query string, same budget as signals/lean.
export const THRESHOLD_READ_MAX_SIGNAL_IDS = 50
// Page size of the batched threshold reads. Without an explicit limit the
// endpoint serves its default page of 25 rows, which silently cut every
// signal's violations to its newest 25 (589 recorded vs 25 drawn, AB#6763).
export const THRESHOLD_READ_PAGE_SIZE = 500

/**
 * Every row of a threshold list endpoint whose `filterParam` matches any of the
 * signal ids: chunked for the query string, paged until the count is reached,
 * de-duplicated by id (a threshold on two signals of different chunks comes
 * back twice). One failed request fails the read, like readSignalLinks: a
 * silently missing chunk would look like signals without thresholds.
 */
const readThresholdRowsBySignals = async <T extends {id: string | number}, E>(
  fetchPage: SignalLinkPageFetch<E>,
  path: string,
  filterParam: string,
  signalIds: string[],
  extraParams: Record<string, string> = {},
): Promise<{data: T[]} | {error: E}> => {
  const readChunk = async (chunk: string[]): Promise<{data: T[]} | {error: E}> => {
    const rows: T[] = []
    let offset = 0
    for (;;) {
      const params = new URLSearchParams(extraParams)
      params.set(filterParam, chunk.join(','))
      params.set('limit', String(THRESHOLD_READ_PAGE_SIZE))
      if (offset > 0) {
        params.set('offset', String(offset))
      }
      // Sequential on purpose: the next offset depends on this page's count.
      // eslint-disable-next-line no-await-in-loop
      const response = await fetchPage(`${path}?${params.toString()}`)
      if (response.error) {
        return {error: response.error}
      }
      const body = response.data as {count?: number; results?: T[]} | T[]
      if (Array.isArray(body)) {
        rows.push(...body)
        return {data: rows}
      }
      const page = body.results ?? []
      rows.push(...page)
      offset += page.length
      if (page.length === 0 || offset >= (body.count ?? 0)) {
        return {data: rows}
      }
    }
  }

  const results = await Promise.all(
    chunkIds(signalIds, THRESHOLD_READ_MAX_SIGNAL_IDS).map((chunk) => readChunk(chunk)),
  )
  const byId = new Map<string | number, T>()
  for (const result of results) {
    if ('error' in result) {
      return result
    }
    for (const row of result.data) {
      byId.set(row.id, row)
    }
  }
  return {data: [...byId.values()]}
}

interface SignalPointsResponse {
  truncated?: boolean
  signals?: Array<{
    signal_id: string
    points?: Array<{timestamp: string; value: number}>
    labels?: SignalValueLabelsInterface
    error?: string
  }>
}

export const lineSignalsAPI = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // getAnomaly: builder.query<AnomalyInterface, { anomalyId: string }>({
    // 	query: ({anomalyId}) => `stepsignalanomalies/${anomalyId}/`,
    // 	// transformResponse: (response: BaseFetchResultsInterface<ViewInterface>) => response.results,
    // }),
    getBatchesByTimeRange: builder.query<
      BatchBasicInterface[],
      {programs: string[]; start: string; end: string}
    >({
      query: ({programs, start, end}) => ({
        url: 'batches/lean/',
        method: 'POST',
        body: {
          programs,
          t_start__gte: start,
          t_end__lte: end,
        },
      }),
      transformResponse: (response: BaseFetchResultsInterface<BatchBasicInterface>) =>
        response.results,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getLastNRelativeToBatch: builder.query<
      BatchInterface[],
      {
        program: string
        batch: string
        limit: number
      }
    >({
      query: ({program, batch, limit}) => ({
        url: 'batches/last_n_rel_to_batch/',
        method: 'POST',
        body: {
          program_id: program,
          batch_id: batch,
          limit,
        },
      }),
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getBatchTrace: builder.query<AsyncTaskScheduledInterface, BatchTraceQueryArgs>({
      query: (args) => buildBatchTraceQueryString(args),
      transformResponse: (response: BaseFetchResultInterface<AsyncTaskScheduledInterface>) =>
        response.result,
      extraOptions: {refetchOnMountOrArgChange: true},
    }),
    getSignalsLean: builder.query<SignalInterface[], SignalsLeanQueryArgs>({
      query: buildSignalsLeanQuery,
      transformResponse: (
        response: BaseFetchResultsInterface<SignalInterface> | SignalInterface[],
      ) => {
        if (Array.isArray(response)) {
          return response
        }
        return response.results
      },
      extraOptions: {refetchOnMountOrArgChange: true},
      providesTags: ['Signal', 'SignalValueDescription'],
    }),

    // signals/lean as a PAGE ({results, count}) — for paginated browsers that
    // need the server total. getSignalsLean above strips the count and many
    // consumers rely on the bare-array shape, so this rides alongside.
    getSignalsLeanPage: builder.query<
      {results: SignalInterface[]; count: number},
      {
        lineId: string | undefined
        equipmentId: string
        limit: number
        offset?: number
        search?: string
      }
    >({
      query: ({lineId, equipmentId, limit, offset, search}) => {
        const params = [
          lineId ? `&machine=${lineId}` : '',
          equipmentId ? `&machine_sub_system=${equipmentId}` : '',
          `&limit=${limit}`,
          offset !== undefined ? `&offset=${offset}` : '',
          search ? `&search=${encodeURIComponent(search)}` : '',
        ]
          .filter(Boolean)
          .join('')

        return `signals/lean/${params ? `?${params.substring(1)}` : ''}`
      },
      transformResponse: (
        response: BaseFetchResultsInterface<SignalInterface> | SignalInterface[],
      ) =>
        Array.isArray(response)
          ? {results: response, count: response.length}
          : {
              results: response.results ?? [],
              count: response.count ?? response.results?.length ?? 0,
            },
      extraOptions: {refetchOnMountOrArgChange: true},
      providesTags: ['Signal', 'SignalValueDescription'],
    }),

    getSignals: builder.query<
      BaseFetchResultsInterface<SignalInterface>,
      {
        lineId?: string
        equipmentId?: string
        // Signals of any of these pieces of equipment, for a list that spans
        // more than one (the lean endpoint takes the same parameter).
        equipmentIds?: readonly string[]
        signalTypeId?: string
        limit?: number
        offset?: number
        search?: string
        sortField?: string
        sortOrder?: 'asc' | 'desc' | null
        // Freshness-derived status filter: regular | irregular | offline | warming_up (AB#5519).
        signalStatus?: string
        noPagination?: boolean
        // true = value-interval (SVT) signals only, false = the rest, null/undefined = no filter.
        hasValueIntervals?: boolean | null
        // Only these data types (?data_type__in=), e.g. numeric signals for a gauge.
        dataTypes?: string[]
        filter?: MuiGridFilterItem | null
      }
    >({
      query: ({
        lineId,
        equipmentId,
        equipmentIds,
        signalTypeId,
        limit,
        offset,
        search,
        sortField,
        sortOrder,
        signalStatus,
        noPagination,
        hasValueIntervals,
        dataTypes,
        filter = null,
      }) => {
        const sortParam =
          sortField && sortOrder ? `&ordering=${sortOrder === 'asc' ? '' : '-'}${sortField}` : ''
        const signalStatusParam = signalStatus ? `&status=${signalStatus}` : ''
        const noPaginationParam = noPagination ? '&no_pagination=true' : ''
        const limitParam = typeof limit === 'number' ? `&limit=${limit}` : ''
        const offsetParam = typeof offset === 'number' ? `&offset=${offset}` : ''
        const machineParam = lineId ? `&machine=${lineId}` : ''
        const machineSubSystemParam = equipmentIds?.length
          ? `&machine_sub_system__in=${equipmentIds.join(',')}`
          : equipmentId
            ? equipmentId === 'unassigned'
              ? '&machine_sub_system__isnull=true'
              : `&machine_sub_system=${equipmentId}`
            : ''
        const signalTypeParam = signalTypeId ? `&signal_type=${signalTypeId}` : ''
        const searchParam = search ? `&search=${encodeURIComponent(search)}` : ''
        const hasValueIntervalsParam =
          typeof hasValueIntervals === 'boolean' ? `&has_value_intervals=${hasValueIntervals}` : ''
        const dataTypesParam = dataTypes?.length ? `&data_type__in=${dataTypes.join(',')}` : ''

        let filterParam = ''
        if (filter) {
          const {field, operator, value} = filter

          if (operator === 'isEmpty' || operator === 'isNotEmpty') {
            const isNull = operator === 'isEmpty' ? 'true' : 'false'
            filterParam = `&${field}__isnull=${isNull}`
          } else if (value != null && !(typeof value === 'string' && value.trim() === '')) {
            const lookup = operatorToLookup[operator ?? '']
            if (lookup) {
              const isForeignKey =
                field === 'machine' || field === 'machine_sub_system' || field === 'signal_type'
              const apiField = isForeignKey ? `${field}__name` : field

              const last = Array.isArray(value) ? value[value.length - 1] : value
              if (operator === 'doesNotContain') {
                filterParam = `&exclude_${apiField}__icontains=${encodeURIComponent(String(last))}`
              } else {
                filterParam = `&${apiField}__${lookup}=${encodeURIComponent(String(last))}`
              }
            }
          }
        }

        const params = [
          machineParam,
          machineSubSystemParam,
          signalTypeParam,
          searchParam,
          sortParam,
          signalStatusParam,
          noPaginationParam,
          limitParam,
          offsetParam,
          hasValueIntervalsParam,
          dataTypesParam,
          filterParam,
        ]
          .filter(Boolean)
          .join('')

        return `signals/${params ? `?${params.slice(1)}` : ''}`
      },
      transformResponse: (
        response: BaseFetchResultsInterface<SignalInterface> | SignalInterface[],
      ): BaseFetchResultsInterface<SignalInterface> => toPaginatedResponse(response),
      extraOptions: {refetchOnMountOrArgChange: true},
      providesTags: ['Signal', 'SignalValueDescription'],
    }),

    getProgramSignalThresholdsAll: builder.query<
      {
        results: ProgramSignalThresholdInterface[]
        count: number
        next: string | null
        previous: string | null
      },
      {
        filters?: {
          productionAreaId?: string
          lineId?: string
          equipmentId?: string
        }
        limit: number
        offset: number
        ordering?: string
        search?: string
      }
    >({
      query: ({filters, limit, offset, ordering, search}) => {
        const params = new URLSearchParams()

        if (filters?.productionAreaId) {
          params.append('triggers__signal__machine__production_area', filters.productionAreaId)
        }
        if (filters?.lineId) {
          params.append('triggers__signal__machine_id', filters.lineId)
        }
        if (filters?.equipmentId) {
          params.append('triggers__signal__machine_sub_system_id', filters.equipmentId)
        }

        params.append('limit', limit.toString())
        params.append('offset', offset.toString())
        const mappedOrdering = mapOrdering(ordering, PROGRAM_SIGNAL_THRESHOLD_ORDERING_MAP)
        if (mappedOrdering) {
          params.append('ordering', mappedOrdering)
        }
        if (search) {
          params.append('search', search)
        }

        return `program-signal-thresholds/${params.toString() ? `?${params}` : ''}`
      },
      providesTags: ['SignalThreshold'],
    }),
    // Resolve specific threshold ids to rows (the subscription scope picker
    // needs names for its selected ids regardless of the searched page). The
    // list endpoint has no id filter, so the ids are fetched individually; an
    // id that no longer resolves (deleted, out of scope) is omitted and the
    // caller renders it as a deleted record.
    getProgramSignalThresholdsByIds: builder.query<
      {results: ProgramSignalThresholdInterface[]},
      {ids: string[]}
    >({
      async queryFn({ids}, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          ids.map((id) => fetchWithBQ({url: `program-signal-thresholds/${id}/`})),
        )
        const results: ProgramSignalThresholdInterface[] = []
        for (const response of responses) {
          if (response.error) {
            if (response.error.status === 404) {
              continue
            }
            return {error: response.error}
          }
          results.push(response.data as ProgramSignalThresholdInterface)
        }
        return {data: {results}}
      },
      providesTags: ['SignalThreshold'],
    }),
    getProgramSignalThresholdsBySignal: builder.query<
      ProgramSignalThresholdInterface[],
      {signalId: string}
    >({
      query: ({signalId}) => `program-signal-thresholds/?triggers__signal__id=${signalId}`,
      transformResponse: (response: BaseFetchResultsInterface<ProgramSignalThresholdInterface>) =>
        response.results,
      keepUnusedDataFor: 300,
      providesTags: ['SignalThreshold'],
    }),
    // The thresholds of several signals in one read (Signal Explorer, AB#6763):
    // one request per chart instead of one per signal, every row (not the
    // endpoint's default first page of 25). Pass the ids sorted, so the cache
    // key does not change when the same signals are only reordered.
    getProgramSignalThresholdsBySignals: builder.query<
      ProgramSignalThresholdInterface[],
      {signalIds: string[]}
    >({
      queryFn: ({signalIds}, _queryApi, _extraOptions, fetchWithBQ) =>
        signalIds.length
          ? readThresholdRowsBySignals<ProgramSignalThresholdInterface, FetchBaseQueryError>(
              fetchWithBQ,
              'program-signal-thresholds/',
              'triggers__signal__id__in',
              signalIds,
            )
          : {data: []},
      keepUnusedDataFor: 300,
      providesTags: ['SignalThreshold'],
    }),
    // The violations of those thresholds overlapping a window, same batching.
    getProgramSignalThresholdViolationsBySignals: builder.query<
      ProgramSignalThresholdViolationInterface[],
      {signalIds: string[]; tStart: string; tEnd: string}
    >({
      queryFn: ({signalIds, tStart, tEnd}, _queryApi, _extraOptions, fetchWithBQ) =>
        signalIds.length
          ? readThresholdRowsBySignals<
              ProgramSignalThresholdViolationInterface,
              FetchBaseQueryError
            >(
              fetchWithBQ,
              'program-signal-threshold-violations/',
              'program_signal_threshold__triggers__signal__id__in',
              signalIds,
              {t_start: tStart, t_end: tEnd},
            )
          : {data: []},
      keepUnusedDataFor: 300,
      providesTags: ['SignalThreshold'],
    }),
    getProgramSignalThresholdViolationsByEquipments: builder.query<
      BaseFetchResultsInterface<ProgramSignalThresholdViolationInterface>,
      {equipmentIds: string[]; tStart: string; tEnd: string; limit: number}
    >({
      query: ({equipmentIds, tStart, tEnd, limit}) => {
        const params = new URLSearchParams({
          program_signal_threshold__triggers__signal__machine_sub_system_id__in:
            equipmentIds.join(','),
          t_start: tStart,
          t_end: tEnd,
          limit: limit.toString(),
        })
        return `program-signal-threshold-violations/?${params}`
      },
      keepUnusedDataFor: 300,
    }),
    // Every violation on a line for a window, for the by-equipment production
    // timeline (AB#5967). Line-scoped rather than equipment-scoped so a row
    // whose equipment ran no batches in the window still shows its violations.
    getProgramSignalThresholdViolationsByLine: builder.query<
      BaseFetchResultsInterface<ProgramSignalThresholdViolationInterface>,
      {lineId: string; tStart: string; tEnd: string; limit: number}
    >({
      query: ({lineId, tStart, tEnd, limit}) => {
        const params = new URLSearchParams({
          program_signal_threshold__triggers__signal__machine_id: lineId,
          t_start: tStart,
          t_end: tEnd,
          limit: limit.toString(),
        })
        return `program-signal-threshold-violations/?${params}`
      },
      keepUnusedDataFor: 300,
    }),
    getProgramSignalThresholdViolations: builder.query<
      {
        results: ProgramSignalThresholdViolationInterface[]
        count: number
        next: string | null
        previous: string | null
      },
      {
        filters?: {
          productionAreaId?: string
          lineId?: string
          equipmentId?: string
        }
        limit: number
        offset: number
        ordering?: string
        search?: string
      }
    >({
      query: ({filters, limit, offset, ordering, search}) => {
        const params = new URLSearchParams()
        if (filters?.productionAreaId) {
          params.append(
            'program_signal_threshold__triggers__signal__machine__production_area',
            filters.productionAreaId,
          )
        }
        if (filters?.lineId) {
          params.append('program_signal_threshold__triggers__signal__machine_id', filters.lineId)
        }
        if (filters?.equipmentId) {
          params.append(
            'program_signal_threshold__triggers__signal__machine_sub_system_id',
            filters.equipmentId,
          )
        }
        params.append('limit', limit.toString())
        params.append('offset', offset.toString())
        const mappedOrdering = mapOrdering(
          ordering,
          PROGRAM_SIGNAL_THRESHOLD_VIOLATION_ORDERING_MAP,
        )
        if (mappedOrdering) {
          params.append('ordering', mappedOrdering)
        }
        if (search) {
          params.append('search', search)
        }

        return `program-signal-threshold-violations/${params.toString() ? `?${params}` : ''}`
      },
    }),
    getProgramSignalThresholdViolationById: builder.query<
      ProgramSignalThresholdViolationInterface,
      {violationId: string}
    >({
      query: ({violationId}) => `program-signal-threshold-violations/${violationId}/`,
    }),

    getErpBatchIdentifier: builder.query<
      {message: string; distinct_values: string[]; oee_url_template: string},
      {batchId: string; machineSubSystemId: string}
    >({
      query: ({batchId, machineSubSystemId}) =>
        `machinesubsystems/${machineSubSystemId}/get_erp_batch_identifier/?batch_id=${batchId}`,
    }),
    getTraces: builder.query<
      TraceResponseInterface,
      {
        signalId: string
        batches: string[]
        relativeMode: boolean
        tSampling: string
        relativeToAnomaly?: string | null
        tStart?: string | null
        tEnd?: string | null
      }
    >({
      query: ({signalId, batches, relativeMode, tSampling, relativeToAnomaly, tStart, tEnd}) => ({
        url: `signals/${signalId}/raw/`,
        method: 'POST',
        body: {
          batch: batches,
          return_relative_timestamp: relativeMode,
          t_sampling: tSampling,
          relative_to_anomaly: relativeToAnomaly,
          t_start_clip: tStart,
          t_end_clip: tEnd,
        },
      }),
      transformResponse: (response: TraceResponseInterface, _, args) => {
        response.data.forEach((trace: TraceInterface) => {
          trace.data.map((item: TraceDataInterface) => {
            if (args.relativeMode === false) {
              item.x = format(new Date(item.x), 'y-MM-d H:mm:ss')
            } else if (item.x[0] === '-') {
              const newNumber = -toSeconds(parse(item.x.split('-')[1])) / 60
              item.x = newNumber.toString()
            } else {
              const newNumber = toSeconds(parse(item.x)) / 60
              item.x = newNumber.toString()
            }
            return item
          })
        })
        return response
      },
      providesTags: (_result, _error, {signalId}) => [{type: 'SignalData' as const, id: signalId}],
      extraOptions: {refetchOnMountOrArgChange: true},
      keepUnusedDataFor: 300,
    }),
    getBatchesTimeline: builder.query<
      ProductionTimelineInterface,
      {
        lineId: string
        equipmentId: string
        summarizeBy: string
        startDate: string
        endDate: string
        anomalyScoreFilter?: number
      }
    >({
      query: ({lineId, equipmentId, summarizeBy, startDate, endDate, anomalyScoreFilter = 0}) =>
        `batches/timeline/?machine=${lineId}${equipmentId ? `&machine_sub_system=${equipmentId}` : ''}&summarize_by=${summarizeBy}&t_start__gte=${startDate}&t_end__lte=${endDate}&anomaly_score__gte=${anomalyScoreFilter}`,
      transformResponse: (response: BaseFetchResultInterface<ProductionTimelineInterface>) =>
        response.result,
    }),
    // Descriptions that name this program explicitly. Descriptions with an
    // EMPTY program list apply to every program on their signal, so callers
    // wanting "steps of program P" also read the program's step signal and keep
    // the empty-list ones (KPI form, AB#6091).
    getDescriptionsByProgram: builder.query<
      SignalDescriptionValueInterface[],
      {programId: string; limit: number; search?: string}
    >({
      query: ({programId, limit, search}) => ({
        url: `descriptions/?programs=${programId}&limit=${limit}${
          search ? `&search=${encodeURIComponent(search)}` : ''
        }`,
        method: 'GET',
      }),
      transformResponse: (response: BaseFetchResultsInterface<SignalDescriptionValueInterface>) =>
        response.results,
      keepUnusedDataFor: 300,
      providesTags: ['SignalValueDescription'],
    }),
    getDescriptionsBySignal: builder.query<
      SignalDescriptionValueInterface[],
      {signalId: string; limit: number; search?: string}
    >({
      query: ({signalId, limit, search}) => ({
        url: `descriptions/?signal=${signalId}&limit=${limit}${
          search ? `&search=${encodeURIComponent(search)}` : ''
        }`,
        method: 'GET',
      }),
      transformResponse: (response: BaseFetchResultsInterface<SignalDescriptionValueInterface>) =>
        response.results,
      keepUnusedDataFor: 300,
      providesTags: ['SignalValueDescription'],
    }),
    // The shared, program-agnostic default descriptions defined once on a
    // SignalType (AB#5800) -- used by a Signal of this type only when it has
    // no description row of its own for a value.
    getDescriptionsBySignalType: builder.query<
      SignalDescriptionValueInterface[],
      {signalTypeId: string; limit: number}
    >({
      query: ({signalTypeId, limit}) => ({
        url: `descriptions/?signal_type=${signalTypeId}&limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (response: BaseFetchResultsInterface<SignalDescriptionValueInterface>) =>
        response.results,
      keepUnusedDataFor: 300,
      providesTags: ['SignalValueDescription'],
    }),
    createDescription: builder.mutation<
      {message: string; result: SignalDescriptionValueInterface},
      {body: Omit<SignalDescriptionValueInterface, 'id'>}
    >({
      query: ({body}) => ({
        url: 'descriptions/',
        method: 'POST',
        body,
      }),
      // The charts' labels and lane colours ride on the signals/values/ reads,
      // which provide this tag, so they refetch (AB#5666, AB#6168).
      invalidatesTags: ['SignalValueDescription'],
    }),
    updateDescription: builder.mutation<
      {message: string; result: SignalDescriptionValueInterface},
      {id: string; body: Omit<SignalDescriptionValueInterface, 'id'>}
    >({
      query: ({id, body}) => ({
        url: `descriptions/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SignalValueDescription'],
    }),
    deleteDescription: builder.mutation<{message: string; result: {id: string}}, string>({
      query: (id) => ({
        url: `descriptions/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SignalValueDescription'],
    }),
    // On-change state intervals for the equipment timeline's signal lanes
    // (AB#5666). One window, N categorical signals; `t_end: null` = still
    // running. per_signal_limit keeps one chatty signal from starving others;
    // `truncated` surfaces when the cap dropped intervals.
    getSignalTimelineIntervals: builder.query<
      {
        truncated: boolean
        intervalsBySignal: Record<string, TimelineLaneInterval[]>
      } & ValueLabelsBySignal,
      {signalIds: string[]; startDate: string; endDate: string}
    >({
      query: ({signalIds, startDate, endDate}) => ({
        url: 'signals/values/',
        method: 'POST',
        body: {
          representation: 'on_change',
          signal_ids: signalIds,
          t_starts: [startDate],
          t_ends: [endDate],
          // Per-signal fairness cap x the 3-lane maximum; `limit` is the
          // endpoint's overall payload backstop.
          per_signal_limit: 500,
          limit: 2500,
        },
      }),
      // Adding a manual value invalidates SignalData, so the lanes refetch
      // instead of sitting on stale intervals for keepUnusedDataFor (AB#5666).
      // The lane labels and colours ride on the read, so a description edit
      // refetches it too.
      providesTags: ['SignalData', 'SignalValueDescription'],
      transformResponse: (response: {
        truncated?: boolean
        signals?: {
          signal_id: string
          intervals: {value: string | number; t_start: string; t_end: string | null}[]
          labels?: SignalValueLabelsInterface
        }[]
      }) => {
        const intervalsBySignal: Record<string, TimelineLaneInterval[]> = {}
        const labelsBySignal: Record<string, Record<string, string>> = {}
        const colorsBySignal: Record<string, Record<string, string>> = {}
        for (const entry of response.signals ?? []) {
          addValueLabels(labelsBySignal, colorsBySignal, entry.signal_id, entry.labels)
          intervalsBySignal[entry.signal_id] = entry.intervals
            .map((interval) => ({
              value: interval.value,
              tStartMs: new Date(interval.t_start).getTime(),
              tEndMs: interval.t_end ? new Date(interval.t_end).getTime() : null,
            }))
            .sort((a, b) => a.tStartMs - b.tStartMs)
        }
        return {
          truncated: Boolean(response.truncated),
          intervalsBySignal,
          labelsBySignal,
          colorsBySignal,
        }
      },
      keepUnusedDataFor: 60,
    }),
    // Stored float samples for the timeline's lab-sample markers (AB#6168).
    // `points`, not `raw`: raw needs a t_sampling_s and reports each survivor at
    // its bucket's END, which moves a sparse lab value away from the time the
    // customer's LIMS shows (AB#5685). Here the timestamp IS the datum, so the
    // read has to hand back the stored one.
    //
    // Any number of signals: a lab equipment flags hundreds (Waldner 1 PRD3:
    // 327), the endpoint takes POINTS_MAX_SIGNAL_IDS per request, so the ids
    // are split into chunks read in parallel and merged. Chunking also gives
    // every chunk its own point budget -- the endpoint's LIMIT is global per
    // request and drops whole trailing signals when it is hit. A chunk that
    // still hits it is split in half and read again, down to single signals:
    // on a 2-week window Waldner 1 PRD3's hourly series filled two of its four
    // chunks, and the 86 signals cut off behind them lost their limits,
    // assessments and Stichproben-IDs (Bechtel, 15.10.). Only a single signal
    // over the budget is reported as `truncated`.
    getSignalPoints: builder.query<
      {
        truncated: boolean
        pointsBySignal: Record<string, Array<{tMs: number; value: number}>>
      } & ValueLabelsBySignal,
      {signalIds: string[]; startDate: string; endDate: string}
    >({
      async queryFn({signalIds, startDate, endDate}, _queryApi, _extraOptions, fetchWithBQ) {
        type PointsRead = Awaited<ReturnType<typeof fetchWithBQ>>
        const readChunk = async (chunk: string[]): Promise<PointsRead[]> => {
          const response = await fetchWithBQ({
            url: 'signals/values/',
            method: 'POST',
            body: {
              representation: 'points',
              signal_ids: chunk,
              t_starts: [startDate],
              t_ends: [endDate],
            },
          })
          const truncated = Boolean((response.data as SignalPointsResponse | undefined)?.truncated)
          if (response.error || !truncated || chunk.length === 1) {
            return [response]
          }
          const half = Math.ceil(chunk.length / 2)
          const parts = await Promise.all([
            readChunk(chunk.slice(0, half)),
            readChunk(chunk.slice(half)),
          ])
          return parts.flat()
        }
        const responses = (
          await Promise.all(chunkIds(signalIds, POINTS_MAX_SIGNAL_IDS).map(readChunk))
        ).flat()
        const pointsBySignal: Record<string, Array<{tMs: number; value: number}>> = {}
        const labelsBySignal: Record<string, Record<string, string>> = {}
        const colorsBySignal: Record<string, Record<string, string>> = {}
        let truncated = false
        for (const response of responses) {
          // One failed chunk fails the read, as the single request did: a strip
          // missing a random hundred signals would look complete and be wrong.
          if (response.error) {
            return {error: response.error}
          }
          const body = response.data as SignalPointsResponse
          truncated = truncated || Boolean(body.truncated)
          for (const entry of body.signals ?? []) {
            // A signal the endpoint could not read carries `error` instead of
            // points; it simply contributes nothing rather than blanking the strip.
            if (!entry.points) {
              continue
            }
            addValueLabels(labelsBySignal, colorsBySignal, entry.signal_id, entry.labels)
            pointsBySignal[entry.signal_id] = entry.points
              .map((point) => ({tMs: new Date(point.timestamp).getTime(), value: point.value}))
              .filter((point) => Number.isFinite(point.tMs))
              .sort((a, b) => a.tMs - b.tMs)
          }
        }
        return {data: {truncated, pointsBySignal, labelsBySignal, colorsBySignal}}
      },
      // A manually entered value invalidates SignalData, so the strip refetches
      // instead of sitting on a stale sample for keepUnusedDataFor; the grade
      // labels ride on the read, so a description edit refetches it too.
      providesTags: ['SignalData', 'SignalValueDescription'],
      keepUnusedDataFor: 60,
    }),
    // The typed links of a set of owner signals (AB#6168): which signal is each
    // one's assessment, upper limit and lower limit. Chunked because the ids
    // travel in the query string and a lab equipment flags hundreds.
    getSignalLinks: builder.query<SignalLinkInterface[], {signalIds: string[]}>({
      queryFn: ({signalIds}, _queryApi, _extraOptions, fetchWithBQ) =>
        // Three roles per owner at most, so a chunk never exceeds one page.
        readSignalLinks(fetchWithBQ, 'signals', signalIds, (chunk) => chunk.length * 3),
      providesTags: ['SignalLink'],
      keepUnusedDataFor: 300,
    }),
    // The other direction (AB#6168): the links that point AT these signals,
    // i.e. where each one is some measurement's limit or assessment. The
    // machine-admin signal list shows both sides; `signals` and
    // `related_signal` AND together on the endpoint, so this is its own read.
    getSignalLinksByRelated: builder.query<SignalLinkInterface[], {signalIds: string[]}>({
      queryFn: ({signalIds}, _queryApi, _extraOptions, fetchWithBQ) =>
        readSignalLinks(fetchWithBQ, 'related_signal', signalIds, () => SIGNAL_LINKS_PAGE_SIZE),
      providesTags: ['SignalLink'],
      keepUnusedDataFor: 300,
    }),
    // A link is never edited in place: changing one is DELETE + POST, and the
    // backend keeps at most one active link per (owner, role).
    createSignalLink: builder.mutation<SignalLinkInterface, Omit<SignalLinkInterface, 'id'>>({
      query: (body) => ({
        url: 'signal-links/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SignalLink'],
    }),
    deleteSignalLink: builder.mutation<void, string>({
      query: (id) => ({
        url: `signal-links/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SignalLink'],
    }),
    // Names (and equipment) of arbitrary signals by id, e.g. the other end of a
    // link that is not on the current page. Lean rows, chunked for the query
    // string; an id that no longer resolves (deleted, out of scope) is omitted.
    getSignalsLeanByIds: builder.query<SignalInterface[], {signalIds: string[]}>({
      async queryFn({signalIds}, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          chunkIds(signalIds, SIGNALS_LEAN_BY_IDS_MAX_SIGNAL_IDS).map((chunk) => {
            const params = new URLSearchParams()
            params.set('ids', chunk.join(','))
            params.set('limit', String(chunk.length))
            return fetchWithBQ(`signals/lean/?${params.toString()}`)
          }),
        )
        const signals: SignalInterface[] = []
        for (const response of responses) {
          if (response.error) {
            return {error: response.error}
          }
          const body = response.data as
            | BaseFetchResultsInterface<SignalInterface>
            | SignalInterface[]
          signals.push(...(Array.isArray(body) ? body : (body.results ?? [])))
        }
        return {data: signals}
      },
      providesTags: ['Signal'],
      keepUnusedDataFor: 300,
    }),
    // On-change intervals of any number of categorical signals in one window,
    // chunked to the endpoint's per-request id cap. The lab-sample strip reads
    // its assessment signals through it: an int signal is interval-backed, so
    // `points` refuses it (AB#6168).
    getSignalIntervalsChunked: builder.query<
      {
        truncated: boolean
        intervalsBySignal: Record<string, TimelineLaneInterval[]>
      } & ValueLabelsBySignal,
      {signalIds: string[]; startDate: string; endDate: string}
    >({
      async queryFn({signalIds, startDate, endDate}, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(
          chunkIds(signalIds, ON_CHANGE_MAX_SIGNAL_IDS).map((chunk) =>
            fetchWithBQ({
              url: 'signals/values/',
              method: 'POST',
              body: {
                representation: 'on_change',
                signal_ids: chunk,
                t_starts: [startDate],
                t_ends: [endDate],
                // A lab status changes per sample, i.e. a few times a day;
                // 100 per signal covers a month of hourly samples.
                per_signal_limit: 100,
                limit: 5000,
              },
            }),
          ),
        )
        const intervalsBySignal: Record<string, TimelineLaneInterval[]> = {}
        const labelsBySignal: Record<string, Record<string, string>> = {}
        const colorsBySignal: Record<string, Record<string, string>> = {}
        let truncated = false
        for (const response of responses) {
          if (response.error) {
            return {error: response.error}
          }
          const body = response.data as SignalIntervalsResponse
          truncated = truncated || Boolean(body.truncated)
          for (const entry of body.signals ?? []) {
            if (!entry.intervals) {
              continue
            }
            addValueLabels(labelsBySignal, colorsBySignal, entry.signal_id, entry.labels)
            intervalsBySignal[entry.signal_id] = entry.intervals
              .map((interval) => ({
                value: interval.value,
                tStartMs: new Date(interval.t_start).getTime(),
                tEndMs: interval.t_end ? new Date(interval.t_end).getTime() : null,
              }))
              .sort((a, b) => a.tStartMs - b.tStartMs)
          }
        }
        return {data: {truncated, intervalsBySignal, labelsBySignal, colorsBySignal}}
      },
      providesTags: ['SignalData', 'SignalValueDescription'],
      keepUnusedDataFor: 60,
    }),
    updateSignals: builder.mutation<
      {message: string; result: SignalInterface[]},
      {id: string; body: Partial<SignalInterface>}
    >({
      query: ({id, body}) => ({
        url: `signals/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Signal'],
    }),
    getSignalById: builder.query<SignalInterface, {signalId: string}>({
      query: ({signalId}) => `signals/${signalId}/`,
      providesTags: (_result, _error, {signalId}) => [{type: 'Signal' as const, id: signalId}],
    }),
    getSignalsByIds: builder.query<SignalInterface[], {signalIds: string[]}>({
      query: ({signalIds}) => signalsByIdsUrl(signalIds),
      transformResponse: (
        response: BaseFetchResultsInterface<SignalInterface> | SignalInterface[],
      ) => (Array.isArray(response) ? response : response.results),
      providesTags: (_result, _error, {signalIds}) => [
        ...signalIds.map((signalId) => ({type: 'Signal' as const, id: signalId})),
        'Signal',
      ],
    }),
    updateBulkSignals: builder.mutation<
      // Every addressed signal, with the values it actually ended up with -- the
      // timeline flags are type-gated per signal, so this is how a caller learns
      // which ones were skipped (AB#6168).
      SignalInterface[] | {message: string},
      {
        signal_ids: string[]
        signal_type?: string
        edge_host_name?: string
        scada_server_name?: string
        criticality_weight?: string | null
        show_in_timeline?: boolean
        show_in_timeline_as_sample?: boolean
      }
    >({
      query: (body) => ({
        url: 'signals/bulk_update_signals/',
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Signal'],
    }),
    deleteSignals: builder.mutation<{message: string}, {ids: string[]}>({
      query: ({ids}) => ({
        url: 'signals/bulk_delete/',
        method: 'DELETE',
        body: {ids},
      }),
      invalidatesTags: ['Signal'],
    }),
    updateIsFavorite: builder.mutation<
      {message: string},
      {signal_ids: string[]; is_favorite: boolean}
    >({
      query: ({signal_ids, is_favorite}) => ({
        url: 'signals/update_is_favorite/',
        method: 'PATCH',
        body: {signal_ids, is_favorite},
      }),
      invalidatesTags: ['Signal'],
    }),
    createSignal: builder.mutation<
      {message: string; result: SignalInterface},
      {
        body: {
          name: string
          machine: string
          machine_sub_system: string
          source_signal: string
          description?: string
        }
      }
    >({
      query: ({body}) => ({
        url: 'signals/simple_create_duplicate_signal/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Signal'],
    }),
    createDerivedSignal: builder.mutation<
      {message: string; result: SignalInterface},
      {
        body: {
          name: string
          description?: string
          machine?: string
          machine_sub_system?: string
          unit?: string
          terms: {
            order: number
            operator?: string
            source_signal?: string
            constant_value?: number
          }[]
        }
      }
    >({
      query: ({body}) => ({
        url: 'signals/create_derived_signal/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Signal'],
    }),
    createManualSignal: builder.mutation<
      {message: string; result: SignalInterface},
      {
        body: {
          name: string
          machine_sub_system: string
          data_type: DataType
          description?: string
          unit?: string
          signal_type?: string
        }
      }
    >({
      query: ({body}) => ({
        url: 'signals/create_manual_signal/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Signal'],
    }),
    addSignalValue: builder.mutation<
      {message: string; result: SignalInterface},
      {id: string; body: {value: string | number | boolean; timestamp?: string}}
    >({
      query: ({id, body}) => ({
        url: `signals/${id}/add_value/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {id}) => [
        {type: 'Signal' as const, id},
        'Signal',
        {type: 'SignalData' as const, id},
      ],
    }),
    getFormulaTerms: builder.query<AggregatorTermInterface[], {signalId: string}>({
      query: ({signalId}) => `signals/${signalId}/formula-terms/`,
      providesTags: (_result, _error, {signalId}) => [
        {type: 'Signal' as const, id: `formula-${signalId}`},
      ],
    }),
    addFormulaTerm: builder.mutation<
      AggregatorTermInterface,
      {
        signalId: string
        body: {
          order: number
          operator?: string
          source_signal?: string
          constant_value?: number
        }
      }
    >({
      query: ({signalId, body}) => ({
        url: `signals/${signalId}/formula-terms/`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, {signalId}) => [
        {type: 'Signal' as const, id: `formula-${signalId}`},
        'Signal',
      ],
    }),
    deleteFormulaTerm: builder.mutation<void, {signalId: string; termId: string}>({
      query: ({signalId, termId}) => ({
        url: `signals/${signalId}/formula-terms/`,
        method: 'DELETE',
        body: {term_id: termId},
      }),
      invalidatesTags: (_result, _error, {signalId}) => [
        {type: 'Signal' as const, id: `formula-${signalId}`},
        'Signal',
      ],
    }),
    getTraceByLine: builder.query<
      EquipmentTraceInterface,
      {
        lineId: string
      }
    >({query: ({lineId}) => `machines/${lineId}/trace/`}),
    createProgramSignalThreshold: builder.mutation<
      {message: string; result: ProgramSignalThresholdInterface},
      {body: ProgramSignalThresholdCreateBody}
    >({
      query: ({body}) => ({
        url: 'program-signal-thresholds/',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['SignalThreshold'],
    }),
    updateProgramSignalThreshold: builder.mutation<
      {message: string; result: ProgramSignalThresholdInterface},
      {id: string; body: Partial<Omit<ProgramSignalThresholdInterface, 'id'>>}
    >({
      query: ({id, body}) => ({
        url: `program-signal-thresholds/${id}/`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['SignalThreshold'],
    }),
    // Rewind a threshold's own check cursor (last_checked_timestamp) and re-evaluate
    // from a chosen date. The equipment equivalent takes ?date=, but this one takes a
    // body: an ISO offset's `+` decodes to a space in a query string, and the parameter
    // of a destructive call has no business being in the URL (AB#5924).
    resetProgramSignalThresholdCheckpoint: builder.mutation<
      {message: string; result: ThresholdCheckpointResetResult},
      {id: string; date: string}
    >({
      query: ({id, date}) => ({
        url: `program-signal-thresholds/${id}/reset-checkpoint/`,
        method: 'POST',
        body: {date},
      }),
      // Only the threshold tag: the violation queries in this slice declare no
      // providesTags at all, so there is nothing to invalidate for them -- the lists
      // that show violations pick the deletions up on their next fetch.
      invalidatesTags: ['SignalThreshold'],
    }),
    deleteProgramSignalThreshold: builder.mutation<{message: string}, {id: string}>({
      query: ({id}) => ({
        url: `program-signal-thresholds/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SignalThreshold'],
    }),
    uploadSignalDescriptionExcel: builder.mutation<
      SignalDescriptionValueUploadResponse,
      {file: FormData}
    >({
      query: ({file}) => ({
        url: 'descriptions/upload-excel/',
        method: 'POST',
        body: file,
      }),
      invalidatesTags: ['SignalValueDescription', 'Signal'],
    }),
    // getBatchesByEquipment: builder.query<BaseFetchResultsInterface<BatchInterface>, {
    // 	equipmentId: string,
    // 	limit?: number,
    // 	offset?: number,
    // }>({
    // 	query: ({equipmentId, limit = 100, offset = 0}) =>
    // 		`batches/?program__machine_sub_system=${equipmentId}&limit=${limit}&offset=${offset}`,
    // 	extraOptions: {refetchOnMountOrArgChange: true},
    // }),
    // getProgramsByEquipment: builder.query<BaseFetchResultsInterface<ProgramInterface>, {
    // 	equipmentId: string,
    // 	limit?: number,
    // 	offset?: number,
    // }>({
    // 	query: ({equipmentId, limit = 100, offset = 0}) =>
    // 		`programs/?machine_sub_system=${equipmentId}&limit=${limit}&offset=${offset}`,
    // 	extraOptions: {refetchOnMountOrArgChange: true},
    // }),
    // getBatchesByPrograms: builder.query<BaseFetchResultsInterface<BatchInterface>, {
    // 	programIds: string[],
    // 	limit?: number,
    // 	offset?: number,
    // }>({
    // 	query: ({programIds, limit = 100, offset = 0}) =>
    // 		`batches/?program=${programIds.join(',')}&limit=${limit}&offset=${offset}`,
    // 	extraOptions: {refetchOnMountOrArgChange: true},
    // }),
  }),

  overrideExisting: false,
})

export const {
  useGetBatchesByTimeRangeQuery,
  useLazyGetLastNRelativeToBatchQuery,
  useGetBatchTraceQuery,
  useGetSignalsQuery,
  useGetSignalsLeanQuery,
  useGetSignalsLeanPageQuery,
  useGetTracesQuery,
  useGetBatchesTimelineQuery,
  useGetTraceByLineQuery,
  useUpdateSignalsMutation,
  useGetSignalByIdQuery,
  useGetSignalsByIdsQuery,
  useLazyGetErpBatchIdentifierQuery,
  useGetDescriptionsBySignalQuery,
  useGetDescriptionsByProgramQuery,
  useGetDescriptionsBySignalTypeQuery,
  useCreateDescriptionMutation,
  useUpdateDescriptionMutation,
  useDeleteDescriptionMutation,
  useGetProgramSignalThresholdsBySignalQuery,
  useGetProgramSignalThresholdsBySignalsQuery,
  useGetProgramSignalThresholdViolationsBySignalsQuery,
  useGetProgramSignalThresholdsAllQuery,
  useLazyGetProgramSignalThresholdsAllQuery,
  useGetProgramSignalThresholdsByIdsQuery,
  useCreateProgramSignalThresholdMutation,
  useUpdateProgramSignalThresholdMutation,
  useDeleteProgramSignalThresholdMutation,
  useResetProgramSignalThresholdCheckpointMutation,
  useGetProgramSignalThresholdViolationsQuery,
  useGetProgramSignalThresholdViolationsByEquipmentsQuery,
  useGetProgramSignalThresholdViolationsByLineQuery,
  useGetProgramSignalThresholdViolationByIdQuery,
  useLazyGetProgramSignalThresholdViolationByIdQuery,
  useLazyGetSignalsQuery,
  useCreateSignalMutation,
  useCreateDerivedSignalMutation,
  useCreateManualSignalMutation,
  useAddSignalValueMutation,
  useGetFormulaTermsQuery,
  useAddFormulaTermMutation,
  useDeleteFormulaTermMutation,
  useDeleteSignalsMutation,
  useUploadSignalDescriptionExcelMutation,
  useUpdateIsFavoriteMutation,
  useUpdateBulkSignalsMutation,
  useGetSignalTimelineIntervalsQuery,
  useGetSignalPointsQuery,
  useGetSignalLinksQuery,
  useGetSignalLinksByRelatedQuery,
  useCreateSignalLinkMutation,
  useDeleteSignalLinkMutation,
  useGetSignalsLeanByIdsQuery,
  useGetSignalIntervalsChunkedQuery,
} = lineSignalsAPI
